import type { Project, Task } from '@/src/renderer/lib/db/models'

/*
  **Favoriten** (#210, CONTEXT.md → Favorit). Projekte und Aufgaben eines Lebensbereichs stehen in
  EINER Reihenfolge in der Gruppe „Favoriten“ der Sidebar; `favoriteOrder` ist ihre Stelle.
  Reine Funktionen — die Sidebar, die Sterne und der Import rechnen alle hiermit.
*/

export type FavoriteItem =
  | { kind: 'project'; id: string; order: number; project: Project }
  | { kind: 'task'; id: string; order: number; task: Task }

/** Wo eine favorisierte Aufgabe steht — das Ziel ihres Klicks in der Gruppe „Favoriten“. */
export type FavoriteTaskPlace =
  | { kind: 'project'; projectId: string }
  | { kind: 'quick-notes' | 'upcoming' | 'dormant' | 'recurring' | 'done' }

/**
 * Mit Projekt: das Projekt. Ohne: Kurz notiert zeigt nur, was kein Startdatum hat und nicht ruht
 * (`isQuickNote`) — auch erledigt, hinter seinem Geschafft-Schalter. Alles andere steht woanders:
 * erledigt auf Geschafft, sonst ruhend auf Ruhend, wiederkehrend auf Wiederkehrend, datiert auf
 * Demnächst (Nutzer-Entscheidung 30.09.2026).
 */
export function favoriteTaskPlace(task: Task): FavoriteTaskPlace {
  if (task.projectId) return { kind: 'project', projectId: task.projectId }
  if (task.startDate == null && task.pausedAt == null) return { kind: 'quick-notes' }
  if (task.status === 'completed') return { kind: 'done' }
  if (task.pausedAt != null) return { kind: 'dormant' }
  if (task.recurrence) return { kind: 'recurring' }
  return { kind: 'upcoming' }
}

/** Ein Favorit unter den Aufgaben: nicht gelöscht, keine Unteraufgabe, mit Stern. */
export function isFavoriteTask(task: Task): boolean {
  return task.favoriteOrder != null && !task.deletedAt && task.parentTaskId == null
}

/**
 * Die Gruppe „Favoriten“ eines Lebensbereichs. `projects` sind die Projekte des Bereichs (wie in
 * der Sidebar, ohne System-Projekte), `lifeSpaceProjectIds` ALLE seine Projekte — eine Aufgabe
 * gehört über ihr Projekt dazu, ohne Projekt über ihren eigenen Bereich (wie Ruhend,
 * `useLifeSpaceTasks`). Erledigte bleiben drin: gemerkt ist gemerkt.
 */
export function favoriteItems({ projects, tasks, lifeSpace, lifeSpaceProjectIds }: {
  projects: readonly Project[]
  tasks: readonly Task[]
  lifeSpace: string
  lifeSpaceProjectIds: ReadonlySet<string>
}): FavoriteItem[] {
  const items: FavoriteItem[] = []
  for (const project of projects) {
    if (project.favoriteOrder == null || project.deletedAt || project.lifeSpace !== lifeSpace) continue
    items.push({ kind: 'project', id: project.id, order: project.favoriteOrder, project })
  }
  for (const task of tasks) {
    if (!isFavoriteTask(task)) continue
    const inSpace = task.projectId == null ? task.lifeSpace === lifeSpace : lifeSpaceProjectIds.has(task.projectId)
    if (inSpace) items.push({ kind: 'task', id: task.id, order: task.favoriteOrder!, task })
  }
  return items.sort((a, b) => a.order - b.order)
}

/**
 * Neu Favorisiertes landet unten: jetzt (ms) — oder knapp hinter dem Letzten, falls der schon
 * später steht (nach unten gezogen, anderes Gerät mit vorgehender Uhr).
 */
export function nextFavoriteOrder(items: readonly { order: number }[], now: number): number {
  const last = items.reduce((max, item) => Math.max(max, item.order), -Infinity)
  return Math.max(now, last + 1)
}

/** Umsortieren: die Mitte zwischen den neuen Nachbarn; ganz oben/unten eins davor/dahinter. */
export function favoriteOrderBetween(before: number | undefined, after: number | undefined, now: number): number {
  if (before === undefined && after === undefined) return now
  if (before === undefined) return after! - 1
  if (after === undefined) return before + 1
  return (before + after) / 2
}

/**
 * Wohin ein gezogener Favorit kommt: `toIndex` zählt in der Liste OHNE ihn. Liefert seine neue
 * Stelle; die anderen behalten ihre (eine Zeile schreibt, nicht die ganze Liste).
 */
export function reorderedFavoriteOrder(items: readonly FavoriteItem[], key: string, toIndex: number, now: number): number {
  const rest = items.filter(item => favoriteKey(item) !== key)
  const index = Math.max(0, Math.min(toIndex, rest.length))
  return favoriteOrderBetween(rest[index - 1]?.order, rest[index]?.order, now)
}

export const favoriteKey = (item: Pick<FavoriteItem, 'kind' | 'id'>): string => `${item.kind}:${item.id}`
